import { z } from "zod";
export const DEPLOYMENT_EFFORTS = ["low", "medium", "high"];
export const deploymentEffortSchema = z.enum(DEPLOYMENT_EFFORTS);
export const EFFORT_LABELS = {
    low: "Bajo — respuestas rápidas, razonamiento corto",
    medium: "Medio",
    high: "Alto — más lento y más caro por turno",
};
/** For tables and badges, where the full label does not fit. */
export const EFFORT_SHORT_LABELS = {
    low: "esfuerzo bajo",
    medium: "esfuerzo medio",
    high: "esfuerzo alto",
};
/**
 * Every model that may appear in `llm_usage_events.model` — which is a superset of
 * what a deployment may select. `EXTRACTION_MODEL` (Haiku, used by the passive
 * extractor and the lead-summary sweep) writes usage rows too and must be priced
 * here, without becoming selectable as a tenant's conversational model.
 */
export const MODEL_CATALOG = {
    "claude-sonnet-4-6": {
        label: "Sonnet 4.6",
        pricing: {
            inputPerMtok: 3.0,
            outputPerMtok: 15.0,
            cacheWritePerMtok: 6.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.3,
        },
        defaultMaxTokens: 1024,
        // Omitting `thinking` already means "no thinking" on 4.6. This is the model the
        // 1024 ceiling was tuned against, so "omit" preserves today's behavior exactly.
        thinking: { mode: "omit" },
        forcedToolChoice: true,
    },
    "claude-sonnet-5": {
        label: "Sonnet 5",
        pricing: {
            inputPerMtok: 2.0,
            outputPerMtok: 10.0,
            cacheWritePerMtok: 4.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.2,
        },
        // Higher than 4.6's because thinking tokens are drawn from the SAME budget.
        // 1024 here is the reply allowance minus however much low-effort reasoning
        // consumed, which is how a turn truncates mid-sentence. max_tokens is a ceiling,
        // not a target — the prompt still caps the reply at four sentences.
        defaultMaxTokens: 4096,
        // Thinking stays ON — disabling it is what leaked reasoning into the visible
        // reply (see ModelThinking above).
        //
        // `medium`, not `low`, and the reason is measured rather than aesthetic. At `low`
        // this model kept ending a turn after its tool calls without writing anything:
        // a prospect who answers with a datum and no question ("Bernardo Gómez") got
        // captured and then met silence, twice in production on the same turn (YT-471,
        // OB-731), with `stop_reason: end_turn` and a thinking block as the entire
        // response. v1 then spends its one text-only retry, which also came back empty.
        //
        // `low` was originally chosen to hold cost down, and that turned out backwards.
        // Output is only ~17% of a warm turn here — the cached prefix is 46% — so the
        // extra reasoning costs about $0.0012, while the retry it avoids costs about
        // $0.011. Measured over the same three-message conversation: 4.7% more per turn
        // at a fixed call count, and 16% LESS in practice, because at `medium` the model
        // writes its reply in the same call as its tools instead of needing a second one.
        //
        // Evidence level, stated so nobody reads more into it than there is: 2 clean runs
        // at `medium` against 2 failures at `low`, on the turn shape that fails, plus 7
        // warm turns of cost data against 4. That is a signal, not a validation — if it
        // regresses, the number to re-measure is the empty-reply rate in
        // docs/CONVERSATION_ENGINE.md §4, not this comment.
        thinking: { mode: "adaptive", effort: "medium" },
        forcedToolChoice: true,
    },
    "claude-sonnet-5-5": {
        label: "Sonnet 5.5",
        pricing: {
            inputPerMtok: 2.0,
            outputPerMtok: 10.0,
            cacheWritePerMtok: 4.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.2,
        },
        // Same tokenizer as Sonnet 5 and the same reason for the higher ceiling: thinking
        // tokens are drawn from this budget too.
        defaultMaxTokens: 4096,
        // Priced but NOT selectable (see SELECTABLE_MODEL_IDS): on our v1 and v2 engines
        // it leaves the turn after a server-executed tool (get_visit_slots, book_visit)
        // with no text. 5.5 returns text written between two tool calls as a
        // progress-update thinking block, empty under `display: "omitted"`, and both
        // engines' text-only retries then come back empty too. Measured locally on
        // 2026-10-12: 1 clean conversation out of 8 — docs/plans/sonnet-5-5-migration.md.
        // `medium` is a placeholder until that is solved; 5.5's effort levels are
        // recalibrated, so Sonnet 5's measurement above does not carry over.
        thinking: { mode: "adaptive", effort: "medium" },
        // 400s on `tool_choice` `tool` / `any`; toolChoiceFor sends `auto` instead.
        forcedToolChoice: false,
    },
    "claude-haiku-4-5": {
        label: "Haiku 4.5",
        pricing: {
            inputPerMtok: 1.0,
            outputPerMtok: 5.0,
            cacheWritePerMtok: 2.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.1,
        },
        defaultMaxTokens: 1024,
        thinking: { mode: "omit" },
        forcedToolChoice: true,
    },
    "claude-haiku-5-5": {
        label: "Haiku 5.5",
        // The ≤100K-token rate card, verified 2026-10-14. Haiku 5.5 is the one model here
        // priced by prompt length: a request whose prompt (cache reads and writes
        // included) is over 100,000 tokens pays 5× on every line. Our cached prefix is
        // ~36K and a conversation's history adds a few thousand, so we price the lower
        // card; a request over the line would be under-reported by `calculateLlmCost`,
        // not refused.
        pricing: {
            inputPerMtok: 0.1,
            outputPerMtok: 0.5,
            cacheWritePerMtok: 0.2, // 2× input (1h TTL)
            cacheReadPerMtok: 0.01,
        },
        // Unlike Haiku 4.5, this model thinks unless told otherwise, and thinking tokens
        // come out of `max_tokens` — the Sonnet 5 ceiling, for Sonnet 5's reason.
        defaultMaxTokens: 4096,
        // Thinking stays ON, for the HC-900 reason in `ModelThinking`: depth is set with
        // `effort`. `medium` is measured, not Anthropic's chat default (`low`): on 12
        // simulated Aloher conversations judged blind it ranked first 9 times against
        // `low` and Sonnet 5 `medium`, and on 5 more it beat `high` 4–1 while answering
        // ~2 s faster (local, 2026-10-14; docs/plans/haiku-5-5-evaluation.md).
        thinking: { mode: "adaptive", effort: "medium" },
        // Accepts `tool` / `any` (and then skips thinking, which a single-purpose call
        // does not need).
        forcedToolChoice: true,
    },
    // Dated alias of the entry above. `EXTRACTION_MODEL` defaults to this spelling
    // (config/env.ts) and it is what existing `llm_usage_events` rows carry; dropping
    // it would silently cost every extraction and lead-summary event at $0. The
    // canonical id has no date suffix — prefer that one for anything new.
    "claude-haiku-4-5-20251001": {
        label: "Haiku 4.5",
        pricing: {
            inputPerMtok: 1.0,
            outputPerMtok: 5.0,
            cacheWritePerMtok: 2.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.1,
        },
        defaultMaxTokens: 1024,
        thinking: { mode: "omit" },
        forcedToolChoice: true,
    },
    // Priced but deliberately not selectable (see `SELECTABLE_MODEL_IDS`): we do not
    // run Opus. The rate is kept correct anyway — a wrong number in the table whose
    // whole job is to be right is worse than no number.
    "claude-opus-4-7": {
        label: "Opus 4.7",
        pricing: {
            inputPerMtok: 5.0,
            outputPerMtok: 25.0,
            cacheWritePerMtok: 10.0, // 2× input (1h TTL)
            cacheReadPerMtok: 0.5,
        },
        // Omitting already means no thinking on 4.7 — same as 4.6, unlike the 5 family.
        defaultMaxTokens: 1024,
        thinking: { mode: "omit" },
        forcedToolChoice: true,
    },
};
/**
 * The models a deployment may be set to, in the order the picker shows them.
 *
 * A strict subset of `MODEL_CATALOG`: the extraction model must be priced without
 * being offered as a tenant's conversational model, Opus is priced without being
 * offered at all, and Sonnet 5.5 is priced but held back until our engines handle its
 * progress-note behavior (see its catalog entry).
 */
export const SELECTABLE_MODEL_IDS = [
    "claude-sonnet-4-6",
    "claude-sonnet-5",
    "claude-haiku-4-5",
    "claude-haiku-5-5",
];
/**
 * Validates a deployment's model. Used as the route body schema on the backend and
 * as the `<option>` list in the CRM, so the two can never disagree — the
 * `whatsappTransportSchema` pattern.
 */
export const selectableModelIdSchema = z.enum(SELECTABLE_MODEL_IDS);
/**
 * `max_tokens` override on a deployment. The ceiling is generous on purpose: this
 * is a guard against a typo, not a cost control — cost is controlled by the model.
 * The floor is where a sales reply starts getting truncated.
 */
export const deploymentMaxTokensSchema = z.number().int().min(256).max(64000);
/**
 * Lookup that accepts any string, because callers hold ids from the environment and
 * from the database — neither of which the type system can narrow. Returns
 * `undefined` for an unknown id; every caller must decide what that means rather
 * than being handed a silent zero.
 */
export function modelCatalogEntry(id) {
    return MODEL_CATALOG[id];
}
/** Human label for a model id, falling back to the raw id for one we no longer list. */
export function modelLabel(id) {
    return modelCatalogEntry(id)?.label ?? id;
}
/**
 * Whether this model runs reasoning on a conversational turn.
 *
 * `effort` only means something when it does — so the CRM uses this to decide
 * whether to offer the control at all, rather than letting someone set a value that
 * would be silently ignored.
 */
export function modelUsesThinking(id) {
    return modelCatalogEntry(id)?.thinking.mode === "adaptive";
}
/** The `max_tokens` this model runs at when the deployment does not override it. */
export function defaultMaxTokensFor(id) {
    return modelCatalogEntry(id)?.defaultMaxTokens;
}
/** The effort this model runs at when the deployment does not override it. */
export function defaultEffortFor(id) {
    const thinking = modelCatalogEntry(id)?.thinking;
    return thinking?.mode === "adaptive" ? thinking.effort : undefined;
}
//# sourceMappingURL=models.js.map